import React from 'react';
import { ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import Modal from '../../ui/Modal';

/**
 * PublishModal Component
 * 
 * Modal for confirming report publish with destination information
 * 
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Function to close modal
 * @param {function} onConfirm - Function to confirm publish
 * @param {string} destination - 'faicon' or 'wordpress'
 * @param {boolean} isUpdate - Whether this is an update (overwrites existing publish)
 * @param {boolean} isPublishing - Whether publish is in progress
 */
const PublishModal = ({
  isOpen,
  onClose,
  onConfirm,
  destination,
  isUpdate = false,
  isPublishing = false
}) => {
  const destinationName = destination === 'faicon' ? 'FAICON' : 'WordPress';
  const destinationDescription = destination === 'faicon' 
    ? 'Your report will be published to the FAICON platform where it can be accessed by authorized users.'
    : 'Your report will be published to WordPress (DigWatch) where it will be publicly accessible.';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isUpdate ? 'Update Published Report' : 'Publish Report'}>
      <div className="space-y-4">
        {/* Destination Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-blue-900 mb-1">
                Publishing to {destinationName}
              </h3>
              <p className="text-sm text-blue-700">
                {destinationDescription}
              </p>
            </div>
          </div>
        </div>

        {/* Update Warning */}
        {isUpdate && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-900 mb-1">
                  Warning: This will overwrite existing publish
                </h3>
                <p className="text-sm text-amber-700">
                  The previously published report will be replaced with the new version. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isPublishing}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPublishing}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              isUpdate
                ? 'bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
          >
            {isPublishing ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Publishing...
              </span>
            ) : (
              isUpdate ? 'Update Publish' : 'Publish'
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PublishModal;

